import {
  contractRouteRefSchema,
  currencySchema,
  Offer,
  offerLoadSummarySchema,
  offerPricingModeSchema,
  OfferSchema,
  offerTargetSchema,
  offerVisibilitySchema,
  orderPricingStrategySchema,
  Order,
  paginationWithSearchSchema,
  objectIdSchema,
  partySchema,
  priceSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../types';

const schema = OfferSchema.model;

/**
 * Shape of one target row at Offer-create time. Mirrors `offerTargetSchema`
 * but trims server-managed fields (`_id`, `status`, `responses`, `acceptedAt`,
 * `counter`).
 */
export const offerCreateTargetAPISchema = z.object({
  party: partySchema,
  pricing: z.object({
    mode: offerPricingModeSchema,
    proposedTotal: priceSchema.optional(),
    currency: currencySchema,
    pricingStrategy: orderPricingStrategySchema.optional(),
    route: contractRouteRefSchema.optional(),
  }),
});
export type OfferCreateTargetAPI = z.infer<typeof offerCreateTargetAPISchema>;

/**
 * Create one Offer.
 *
 * Server enforces:
 *   - `visibility='public_load_board'` is only allowed when the creator is
 *     `Order.shipper` (the originator). Brokers downstream are capped at
 *     `'targeted'` / `'private_network'`.
 *   - `proposedBuyer` must have a buy-side accepted Contract on `orderId`
 *     OR be `Order.shipper` itself (originator-self-broadcast).
 *   - `loadSummary` is snapshotted server-side from the parent Order (input
 *     is ignored if supplied).
 */
export const offerCreateAPISchema = z.object({
  orderId: objectIdSchema,
  proposedBuyer: partySchema,
  visibility: offerVisibilitySchema,
  targets: z.array(offerCreateTargetAPISchema).default([]),
  expiresAt: z.coerce.date().optional(),
});
export type OfferCreateAPI = API<z.infer<typeof offerCreateAPISchema>, Offer>;

/**
 * Per-target pricing preview (no Offer is inserted). The buyer reviews the
 * suggested prices and may override before confirming via `OfferCreateAPI`.
 *
 * v2 takes a full `LaneQuery` so the pricing resolver can run the directed /
 * seller-own / buyer-own cascade per candidate. The `routeId` shortcut is
 * still supported via `lane.routeId` on the explicit-pin payload.
 */
export const offerPreviewBroadcastAPISchema = z.object({
  orderId: objectIdSchema,
  proposedBuyer: partySchema,
  candidates: z.array(partySchema).min(1),
  lane: z.object({
    destinations: z
      .array(
        z.object({
          type: z.string(),
          center: z.unknown(),
        }),
      )
      .min(2),
    vehicle: z.string(),
    temperatureType: z.array(z.string()),
    tripType: z.string(),
    loadType: z.string(),
    routeId: objectIdSchema.optional(),
  }),
});
export type OfferPreviewBroadcastAPI = API<
  z.infer<typeof offerPreviewBroadcastAPISchema>,
  Array<{
    party: z.infer<typeof partySchema>;
    name?: string;
    route?: z.infer<typeof contractRouteRefSchema>;
    suggestedPrice?: number;
    suggestedMode: 'directed' | 'seller_own' | 'buyer_own' | 'needs_manual';
  }>
>;

/** Inbox: Offers where the viewer is a target row in {open, counter_proposed}. */
export const offerGetInboxAPISchema = paginationWithSearchSchema.extend({
  /** when set, narrows to a specific Order. */
  orderId: objectIdSchema.optional(),
  /** when true, include `expired` / `withdrawn` rows for audit lookups. */
  includeClosed: z.boolean().default(false),
});
export type OfferGetInboxAPI = API<z.infer<typeof offerGetInboxAPISchema>, Offer[]>;

/** Outbox: Offers the viewer (proposedBuyer) created. */
export const offerGetOutboxAPISchema = paginationWithSearchSchema.extend({
  orderId: objectIdSchema.optional(),
  status: schema.shape.status.optional(),
});
export type OfferGetOutboxAPI = API<z.infer<typeof offerGetOutboxAPISchema>, Offer[]>;

/** Get one Offer (projection applied server-side). */
export const offerGetOneAPISchema = z.object({
  _id: objectIdSchema,
});
export type OfferGetOneAPI = API<z.infer<typeof offerGetOneAPISchema>, Offer>;

/**
 * Privileged Order preview for a target carrier reviewing an incoming Offer.
 * Bypasses the `Order.tenants[]` gate by validating the viewer appears in
 * `offer.targets[].party._id` with an active status (`open`,
 * `counter_proposed`, or `accepted`).
 */
export const offerGetOrderPreviewAPISchema = z.object({
  offerId: objectIdSchema,
});
export type OfferGetOrderPreviewAPI = API<
  z.infer<typeof offerGetOrderPreviewAPISchema>,
  Order
>;

/** Accept one target row. Spawns a Contract on the parent Order. */
export const offerAcceptTargetAPISchema = z.object({
  offerId: objectIdSchema,
  targetId: objectIdSchema,
});
export type OfferAcceptTargetAPI = API<
  z.infer<typeof offerAcceptTargetAPISchema>,
  { offer: Offer; order: Order }
>;

/** Counter-propose on one target row. Never touches the Order. */
export const offerCounterTargetAPISchema = z.object({
  offerId: objectIdSchema,
  targetId: objectIdSchema,
  proposedPrice: priceSchema,
  note: z.string().optional(),
});
export type OfferCounterTargetAPI = API<z.infer<typeof offerCounterTargetAPISchema>, Offer>;

/** Decline one target row. Never touches the Order. */
export const offerDeclineTargetAPISchema = z.object({
  offerId: objectIdSchema,
  targetId: objectIdSchema,
  note: z.string().optional(),
});
export type OfferDeclineTargetAPI = API<z.infer<typeof offerDeclineTargetAPISchema>, Offer>;

/** Withdraw one target row (buyer-initiated). */
export const offerWithdrawTargetAPISchema = z.object({
  offerId: objectIdSchema,
  targetId: objectIdSchema,
  note: z.string().optional(),
});
export type OfferWithdrawTargetAPI = API<z.infer<typeof offerWithdrawTargetAPISchema>, Offer>;

/** Withdraw the entire Offer (buyer-initiated). All open rows are withdrawn. */
export const offerWithdrawAPISchema = z.object({
  offerId: objectIdSchema,
  note: z.string().optional(),
});
export type OfferWithdrawAPI = API<z.infer<typeof offerWithdrawAPISchema>, Offer>;

/** Public load board search (future). Lane filters operate on `loadSummary.route`. */
export const offerLoadBoardSearchAPISchema = paginationWithSearchSchema.extend({
  from: z.string().optional(),
  to: z.string().optional(),
});
export type OfferLoadBoardSearchAPI = API<
  z.infer<typeof offerLoadBoardSearchAPISchema>,
  Offer[]
>;

// Re-exports so consumers can build typed shapes from the same module.
export {
  offerLoadSummarySchema,
  offerTargetSchema,
  offerVisibilitySchema,
  offerPricingModeSchema,
};
